// The per-practice identity: the `PracticeId` a surface carries as its
// `data-practice` attribute to paint in that practice's accent. Backend
// practice names arrive as the `Practice` enum's string form and map onto a
// `PracticeId` here, so every surface that renders a server-side practice
// paints it the same way.
//
// This is PURELY frontend. It never travels to the backend, never affects
// generation, never appears in the AI prompt — it just paints the page so each
// practice feels distinct in the speaker's hand.

import { Practice } from '@voicedojo/backend-types';

/** The seven practice identifiers used as `data-practice` attribute values. */
export type PracticeId =
  | 'instrument'
  | 'archetype-play'
  | 'body-language'
  | 'riff'
  | 'record-review'
  | 'storytelling'
  | 'active-listening';

/**
 * Maps a backend `Models.Sessions.Practice` enum name (the string form the
 * API serialises via `JsonStringEnumConverter` — "TheInstrument",
 * "ArchetypePlay", …) onto the frontend `PracticeId` used for theming + the
 * `data-practice` attribute. Centralised here so every surface that renders a
 * server-side practice (My library cards, session-detail chrome) paints
 * with the same swatch. An unknown name falls back to `'instrument'` so a new
 * backend practice can't crash the renderer before its theme lands.
 */
export function practiceIdFor(practice: string): PracticeId {
  switch (practice) {
    case Practice.TheInstrument:
      return 'instrument';
    case Practice.ArchetypePlay:
      return 'archetype-play';
    case Practice.BodyLanguagePlay:
      return 'body-language';
    case Practice.Riff:
      return 'riff';
    case Practice.Storytelling:
      return 'storytelling';
    case Practice.RecordAndReview:
      return 'record-review';
    case Practice.ActiveListening:
      return 'active-listening';
    default:
      return 'instrument';
  }
}

/** Which of the product's two themes a practice sits in (product-vision.md). */
export const PRACTICE_THEME: Record<PracticeId, 'playground' | 'mastery'> = {
  instrument: 'playground',
  'archetype-play': 'playground',
  'body-language': 'playground',
  riff: 'playground',
  storytelling: 'mastery',
  'record-review': 'mastery',
  'active-listening': 'mastery',
};

// Here rather than in the (practice) layout because the layout is no longer its
// only reader — the shared active view derives the practice from the route too.
/** SvelteKit route id → the practice it renders. */
export const PRACTICE_ROUTE: Record<string, PracticeId> = {
  '/(practice)': 'instrument',
  '/(practice)/archetypes': 'archetype-play',
  '/(practice)/body-language': 'body-language',
  '/(practice)/word-play': 'riff',
  '/(practice)/storytelling': 'storytelling',
  '/(practice)/active-listening': 'active-listening',
  '/(practice)/rehearsal': 'record-review',
  '/(practice)/rehearsal/series': 'record-review',
  '/(practice)/rehearsal/series/[id]': 'record-review',
  '/(practice)/progress/plans': 'record-review',
  // The dev-only geometry harness, so the shared view builds its pacer toggle.
  '/(practice)/dev-focus': 'instrument',
};
